import React, { useState } from 'react';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { CircularProgress, FormHelperText, Grid, TextField } from '@mui/material';
import CommonInput from '../../common/input/InputBox';
import CommonButton from '../../common/button/Button';
import axios from "axios";
import useSnackbarAlert from "../../../utils/snackbarHandlers";
import { useNavigate } from "react-router-dom";
import "./createAdForm.css";
import UploadImage from "../../common/uploadImage/UploadImage";

interface AdDetails {
  productTitle: string;
  productPriceDescription: string;
  adLocation: string;
  image: string;
};

const initialData: AdDetails = {
  productTitle: "",
  productPriceDescription: "",
  adLocation: "",
  image: "",
};

interface AdvertisementsFormProps {
  adsData?: any;
  edit?: boolean;
  formSubmit?: (value: AdDetails) => void
}

const AdvertisementsForm: React.FC<AdvertisementsFormProps> = ({ adsData = initialData, edit = false, formSubmit }) => {
  const { t } = useTranslation('translation');
  const [loading, setLoading] = useState<boolean>(false);
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();
  const [image, setImage] = React.useState<string>("");
  const [imageLoading, setImageLoading] = useState(false);
  const uId = JSON.parse(localStorage.getItem('UserData') || "");
  const navigate = useNavigate();

  const validationSchema = yup.object().shape({
    productTitle: yup
      .string()
      .required(t('productTitleRequired')),
    productPriceDescription: yup
      .string()
      .required(t('productPriceDescriptionRequired'))
      .test("len", t("productPriceDescriptionRequired"), (val) => val !== " "),
    adLocation: yup
      .string()
      .required(t('adLocationRequired')),
    image: yup
      .mixed()
      .required(t("imageRequired")),
  });

  const onSubmit = (value: AdDetails) => {
    setLoading(true);
    const fileHeaders = {
      accept: "application/json",
      "Accept-Language": "en-US,en;q=0.8",
      "Content-Type": "multipart/form-data",
    };
    const body = {
      productTitle: value.productTitle,
      productPriceDescription: value.productPriceDescription,
      adLocation: value.adLocation,
      image: value.image,
      userId: uId ? uId?.uId : "",
      lastUpdatedBy: uId ? uId?.uId : "",
    };

    axios
      .post(`${apiBaseURL}/api/advertisement/addAdvertisement`, body, { headers: fileHeaders })
      .then((data: any) => {
        if (data?.status === 200) {
          showSuccessMessage(t("adCreatedSuccessfully", "Advertisement created successfully"))
          navigate('/shopOwner/advertisements');
        }
      })
      .catch((err: any) =>
        showErrorMessage(t("somethingWentWrong"))
      )
      .finally(() => {
        setLoading(false);
      });
  }

  const formik = useFormik({
    initialValues: edit ? adsData : initialData,
    onSubmit: onSubmit,
    validationSchema: validationSchema,
    enableReinitialize: true,
  });

  const { values, errors, touched, handleSubmit, handleChange, setFieldValue } = formik;

  const options: any = [
    { value: " ", key: " " },
    { value: "HEADER", key: "Header" },
    { value: "FOOTER", key: "Footer" },
  ];

  const handleImageUpload = (event: any) => {
    const file = URL.createObjectURL(event?.target?.files[0]);
    setImage(file);
    const fileFound = event.target.type === "file" && event.target.files[0];
    setFieldValue("image", fileFound);
  };

  const onLoad = () => {
    setImageLoading(false);
  };

  React.useEffect(() => {
    setFieldValue("image", adsData?.imageUrl); 
    setImage(adsData?.imageUrl)
  }, [adsData?.imageUrl])

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <CommonInput
            id="ad-title-input-box"
            fullWidth
            required
            name="productTitle"
            className="landing-input-box"
            label={t("productTitle")}
            onChange={handleChange}
            disabled={edit}
            placeholder={t('enterProductTitle')}
            value={values?.productTitle}
            error={touched?.productTitle && errors?.productTitle ? true : false}
            helperText={errors?.productTitle && touched?.productTitle ? errors?.productTitle : ""}
          />
        </Grid>
        <Grid item xs={6}>
          <CommonInput
            id="product-price-input-box"
            fullWidth
            required
            name="productPriceDescription"
            className="landing-input-box"
            label={t("productPriceDescription")}
            placeholder={t('enterProductPriceDescription')}
            onChange={handleChange}
            value={values?.productPriceDescription}
            error={touched?.productPriceDescription && errors?.productPriceDescription ? true : false}
            helperText={errors?.productPriceDescription && touched?.productPriceDescription ? errors?.productPriceDescription : ""}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            id="ad-location-input-box"
            fullWidth
            required
            name="adLocation"
            label={t("adLocation")}
            sx={{ width: "70%", height: "15%"}}
            variant="standard"
            onChange={handleChange}
            value={values?.adLocation}
            error={touched?.adLocation && errors?.adLocation ? true : false}
            helperText={errors?.adLocation && touched?.adLocation ? errors?.adLocation : ""}
            select
            SelectProps={{
              native: true
            }}
          >
            {options.map((option: any, index: any) => (
              <option key={index} value={option.value}>
                {option.key}
              </option>
            ))}
          </TextField>
        </Grid>
      </Grid>
      <Grid className='relative' mt={4}>
        <Grid>
          <Grid
            className={`${touched?.image && errors?.image ? "text-red-700" : "text-[#00000099]"}`}
          >
            {t("adImage")}
          </Grid>
        </Grid>
        <UploadImage
          name='image'
          onChange={(event: any) => handleImageUpload(event)}
          accept='.png,.jpg,.jpeg'
        />
        {!image ? (
          <Grid>
            <Grid className='bg-[#f1f0ee] mt-1 w-[50%] text-[#00000099] text-[0.8rem] h-[80px] flex justify-center items-center rounded-[8px]'>
              {t("uploadTicketImage")}
            </Grid>
            <FormHelperText
              error={touched?.image && errors?.image ? true : false}
              className='input-helper-text'
            >
              {errors?.image && touched?.image ? errors?.image : ""}
            </FormHelperText>
          </Grid>
        ) : <Grid></Grid>}
        {!errors?.image ? (
          <Grid item xs={6} className='h-[110px]' ml={2}>
            {image && (
              <img
                alt="lottery"
                src={image}
                onLoad={onLoad}
                className='w-auto h-full aspect-square object-cover rounded-[20%] mt-2'
              />
            )}
            {imageLoading && (
              <CircularProgress
                color='inherit'
                size={14}
                thickness={6}
                style={{ marginLeft: "10%" }}
              />
            )}
          </Grid>
        ) : <Grid item xs={6} className='h-[110px]'></Grid>}
      </Grid>
      <Grid>
        {!edit && <CommonButton
          id="create-ad-button"
          className="ad-button"
          variant="contained"
          disabled={loading}
          onClick={() => {
            handleSubmit();
          }}
        >
          {t('submit')}
          {loading && (
            <CircularProgress
              color="inherit"
              size={14}
              thickness={6}
              style={{ marginLeft: "8px" }}
            />
          )}
        </CommonButton>}

        {edit && <button
          id="create-ad-update-button"
          className="ad-button"
          disabled={loading}
          onClick={() => formSubmit && formSubmit(values)}
        >
          {t('update')}
          {loading && (
            <CircularProgress
              color="inherit"
              size={14}
              thickness={6}
              style={{ marginLeft: "8px" }}
            />
          )}
        </button>}
      </Grid>
    </>
  )
}

export default AdvertisementsForm;